/*
 * cm-video-autoplay-fix — hides the <video> entirely while its autoplay is
 * blocked, and retries on the first real user gesture.
 *
 * iOS Low Power Mode blocks `<video autoplay muted playsinline>` outright,
 * even though every attribute needed for autoplay elsewhere is already
 * correct — it's a deliberate battery-saving restriction, not a bug in the
 * markup. WebKit's own bug tracker confirms this is "by design": whenever
 * autoplay is blocked this way, Safari always draws a big native play
 * button over the element, and it lives in shadow DOM specifically so page
 * CSS can't reach it (webkit.org/show_bug.cgi?id=219889 — filed as a bug,
 * closed "Won't Fix"). The only way to not show that button is to not have
 * a <video> element on screen at all while it's blocked — so this swaps in
 * a plain <img> of the poster instead, which carries no such affordance,
 * and swaps back once play() actually succeeds.
 */
(() => {
  const candidates = Array.from(document.querySelectorAll('video[autoplay]'));
  if (!candidates.length) return;

  const showPosterFallback = (video) => {
    if (video._cmFallbackImg) return;
    const poster = video.getAttribute('poster');
    if (!poster) return;
    const img = document.createElement('img');
    img.src = poster;
    img.alt = '';
    img.setAttribute('aria-hidden', 'true');
    img.className = video.className;
    video.insertAdjacentElement('afterend', img);
    video.style.display = 'none';
    video._cmFallbackImg = img;
  };

  const hidePosterFallback = (video) => {
    if (!video._cmFallbackImg) return;
    video._cmFallbackImg.remove();
    video._cmFallbackImg = null;
    video.style.display = '';
  };

  const stalled = new Set();

  candidates.forEach((video) => {
    const attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(() => {
        stalled.add(video);
        showPosterFallback(video);
      });
    }
    // Autoplay can also be silently blocked without the promise rejecting
    // (older WebKit) — checking shortly after catches that case too.
    setTimeout(() => {
      if (video.paused) {
        stalled.add(video);
        showPosterFallback(video);
      }
    }, 300);
  });

  const retry = () => {
    stalled.forEach((video) => {
      if (!video.paused) return;
      video
        .play()
        .then(() => hidePosterFallback(video))
        .catch(() => {});
    });
    stalled.clear();
    document.removeEventListener('touchstart', retry);
    document.removeEventListener('click', retry);
    document.removeEventListener('scroll', retry);
  };

  document.addEventListener('touchstart', retry, { passive: true, once: true });
  document.addEventListener('click', retry, { once: true });
  document.addEventListener('scroll', retry, { passive: true, once: true });
})();
